import { type Translate, useT } from "~/i18n";
import { type MessageKey, ptBR } from "~/i18n/messages/pt-BR";

export type StatusTone = "neutral" | "info" | "positive" | "caution" | "critical";

export type StatusDomain =
  | "evidence"
  | "proposal"
  | "ruleset"
  | "source"
  | "run"
  | "calculation"
  | "job"
  | "audit";

export type StatusDescriptor = {
  tone: StatusTone;
  /** Rendered as text, so it survives forced-colours and high-contrast modes. */
  glyph: string;
};

/**
 * Status vocabulary is contract vocabulary. The token stays on `data-status`,
 * `title` and the detail fact line so a ticket, the audit log and the payload
 * still agree. What the operator reads is the catalogue label.
 *
 * Every pill carries tone + glyph + label. Colour is the third signal, never
 * the only one — WCAG 2.2 §1.4.1.
 */
export const DESCRIPTORS: Record<string, StatusDescriptor> = {
  // Evidence review
  NEW: { tone: "info", glyph: "●" },
  IN_REVIEW: { tone: "caution", glyph: "◐" },
  APPROVED: { tone: "positive", glyph: "✓" },
  REJECTED: { tone: "critical", glyph: "✕" },
  DUPLICATE: { tone: "neutral", glyph: "⧉" },

  // Ruleset / proposal lifecycle
  DRAFT: { tone: "neutral", glyph: "◌" },
  PENDING_REVIEW: { tone: "caution", glyph: "◐" },
  PUBLISHED: { tone: "positive", glyph: "▲" },
  SUPERSEDED: { tone: "neutral", glyph: "↧" },

  // Sources
  ACTIVE: { tone: "positive", glyph: "▶" },
  PAUSED: { tone: "caution", glyph: "❚❚" },
  DEGRADED: { tone: "critical", glyph: "▼" },
  DISABLED: { tone: "neutral", glyph: "⊘" },

  // Ingestion runs
  QUEUED: { tone: "neutral", glyph: "◌" },
  DISCOVERING: { tone: "info", glyph: "◍" },
  FETCHING: { tone: "info", glyph: "↓" },
  EXTRACTING: { tone: "info", glyph: "⇢" },
  WAITING_REVIEW: { tone: "caution", glyph: "◐" },
  STALLED: { tone: "critical", glyph: "!" },
  SUCCEEDED: { tone: "positive", glyph: "✓" },
  PARTIAL: { tone: "caution", glyph: "◑" },
  FAILED: { tone: "critical", glyph: "✕" },
  CANCELLED: { tone: "neutral", glyph: "⊘" },

  // Source-run receipt disposition. Identity of a document, never a tax rate.
  CREATED: { tone: "info", glyph: "◇" },
  REUSED: { tone: "neutral", glyph: "↻" },

  // Calculation items
  CALCULATED: { tone: "positive", glyph: "✓" },
  REVIEW_REQUIRED: { tone: "caution", glyph: "⚑" },

  // Calculation jobs (fiscal tasks, stored calculation groups, unrun uploads)
  RUNNING: { tone: "info", glyph: "◍" },
  COMPLETED: { tone: "positive", glyph: "✓" },
  IMPORTED: { tone: "neutral", glyph: "↥" },
  INVALID: { tone: "critical", glyph: "⊘" },

  // Calculation coverage of a route: is there a published rule for it?
  COVERED: { tone: "positive", glyph: "✓" },
  MISSING: { tone: "critical", glyph: "✕" },
  STALE: { tone: "caution", glyph: "◐" },
  NEEDS_REVIEW: { tone: "caution", glyph: "⚑" },

  // Audit outcome
  ALLOWED: { tone: "positive", glyph: "✓" },
  DENIED: { tone: "critical", glyph: "✕" },
};

/** Tokens that have a catalogue label for each domain that uses them. */
export const STATUS_DOMAIN_TOKENS: Record<StatusDomain, readonly string[]> = {
  evidence: ["NEW", "IN_REVIEW", "APPROVED", "REJECTED", "DUPLICATE"],
  proposal: ["DRAFT", "PENDING_REVIEW", "APPROVED", "REJECTED", "PUBLISHED"],
  ruleset: ["DRAFT", "IN_REVIEW", "APPROVED", "PUBLISHED", "SUPERSEDED", "REJECTED"],
  source: ["ACTIVE", "PAUSED", "DEGRADED", "DISABLED"],
  run: [
    "QUEUED",
    "DISCOVERING",
    "FETCHING",
    "EXTRACTING",
    "WAITING_REVIEW",
    "STALLED",
    "SUCCEEDED",
    "PARTIAL",
    "FAILED",
    "CANCELLED",
    "CREATED",
    "REUSED",
  ],
  calculation: ["CALCULATED", "REVIEW_REQUIRED"],
  job: [
    "IMPORTED",
    "INVALID",
    "QUEUED",
    "RUNNING",
    "COMPLETED",
    "SUCCEEDED",
    "REVIEW_REQUIRED",
    "WAITING_REVIEW",
    "PARTIAL",
    "FAILED",
    "CANCELLED",
  ],
  audit: ["ALLOWED", "DENIED"],
};

function isMessageKey(key: string): key is MessageKey {
  return Object.hasOwn(ptBR, key);
}

export function statusMessageKey(status: string, domain?: StatusDomain): MessageKey | null {
  const domainKey = domain ? `status.${domain}.${status}` : "";
  if (domainKey && isMessageKey(domainKey)) return domainKey;
  const genericKey = `status.${status}`;
  if (isMessageKey(genericKey)) return genericKey;
  return null;
}

export function statusVisibleLabel(
  t: Translate,
  status: string,
  domain?: StatusDomain,
  label?: string,
): string {
  if (label) return label;
  const key = statusMessageKey(status, domain);
  return key ? t(key) : status;
}

export function describeStatus(status: string): StatusDescriptor {
  return DESCRIPTORS[status] ?? { tone: "neutral", glyph: "•" };
}

/**
 * One badge size everywhere (20px, 12px text). `size="live"` is the larger
 * live-run pill reserved for Agentes and Pesquisa web headers.
 */
export function StatusPill({
  status,
  title,
  label,
  domain,
  size,
}: {
  status: string;
  title?: string;
  label?: string;
  domain?: StatusDomain;
  size?: "live";
}) {
  const t = useT();
  const descriptor = describeStatus(status);
  const visible = statusVisibleLabel(t, status, domain, label);

  return (
    <span
      className="status-pill"
      data-tone={descriptor.tone}
      data-status={status}
      data-size={size}
      // The tooltip reads as the label in the person's language; the contract token stays on
      // data-status for tickets.
      title={title ?? visible}
    >
      <span className="status-pill__glyph" aria-hidden="true">
        {descriptor.glyph}
      </span>
      <span className="sr-only">{t("a11y.statusLabel", { label: visible })}</span>
      <span aria-hidden="true">{visible}</span>
    </span>
  );
}

/** Compact inline dot + label, for dense cells where a pill is too heavy. */
export function StatusText({ status, domain }: { status: string; domain?: StatusDomain }) {
  const t = useT();
  const descriptor = describeStatus(status);
  const visible = statusVisibleLabel(t, status, domain);
  return (
    <span
      className="row row--tight"
      data-tone={descriptor.tone}
      data-status={status}
      title={visible}
    >
      <span aria-hidden="true">{descriptor.glyph}</span>
      <span className="sr-only">{t("a11y.statusLabel", { label: visible })}</span>
      <span aria-hidden="true" className="text-2xs">
        {visible}
      </span>
    </span>
  );
}

/**
 * Header fact cell: the catalogue label only. The contract token stays on
 * `data-status` for tickets, never as visible copy or tooltip.
 */
export function StatusFact({ status, domain }: { status: string; domain: StatusDomain }) {
  const t = useT();
  const label = statusVisibleLabel(t, status, domain);
  return (
    <div>
      <dt className="factline__label">{t("common.status")}</dt>
      <dd className="factline__value" data-status={status} title={label}>
        {label}
      </dd>
    </div>
  );
}
